import { ActionType } from '@enums/node-types.enum';
import {
  getBaseCssVariables,
  PrizeType,
} from '@longnecktech/splash-commons-fe';
import { ConfigValue } from '@longnecktech/splash-commons-fe/dist/types/config-value';
import { BoardConfig } from '@models/board-config.interface';
import { CollectibleInfo } from '@models/collectibles';
import {
  GameConfig,
  StageInfo,
  GameResponse,
  LayoutAsset,
  ActionInfo,
  CssVariable,
  GameStartPosition,
  GoalState,
} from '@models/game-config.interface';
import { UserPrize } from '@models/prize';
import { Rules } from '@models/rules';
import { SessionParams } from '@models/session-params';
import { TranslationService } from '@services/core/TranslationService';
import { Singleton } from '@utils/singleton';

const _COLOR_KEYS = [
  '--color-base-1',
  '--color-accent-2',
  '--color-accent-1',
  '--color-neutral-100',
  '--color-success',
  '--color-warning',
  '--color-informative',
  '--color-error',
  '--z-cta-buttons-primary-background-color',
  '--z-cta-buttons-primary-text-color',
  '--z-pad-outline-color',
  '--z-dice-btn-background',
  '--z-dice-overlay-background',
  '--z-dice-result-text-color',
  '--z-result-page-text-color',
] as const;

export type ColorKey = (typeof _COLOR_KEYS)[number];

@Singleton
export class GameConfigService {
  private gameConfig: GameConfig;
  private _sessionParams: SessionParams;
  private _colors: Record<string, string> = {};
  private _gameResponse: GameResponse | null = null;

  get sessionParams(): SessionParams {
    return this._sessionParams;
  }

  get gameResponse(): GameResponse | null {
    return this._gameResponse;
  }

  get gameGoals(): GoalState[] {
    return this._gameResponse?.goals || [];
  }

  get gameStartPosition(): GameStartPosition {
    return {
      tile: this._gameResponse?.startPosition?.tile ?? 0,
      nextRoll: this._gameResponse?.startPosition?.nextRoll ?? 0,
      rollsAvailable: this._gameResponse?.startPosition?.rollsAvailable ?? 0,
    };
  }

  get gameUuid(): string {
    return this.gameConfig?.game?.uuid ?? '';
  }

  get currentStage(): StageInfo | undefined {
    return this.gameConfig?.stage;
  }

  get collectibles(): CollectibleInfo[] {
    return this.currentStage?.collectiblesConfig?.collectibles ?? [];
  }

  get actions(): ActionInfo[] {
    return this.currentStage?.actionsConfig?.actions ?? [];
  }

  get actionNodes(): Record<string, string> {
    return this.currentStage?.pathConfig?.nodes ?? {};
  }

  get prizes(): UserPrize[] {
    return (
      this.currentStage?.actionsConfig?.actions
        .filter((a) => !!a.prize)
        .map((a) => a.prize!) ?? []
    );
  }

  get layoutAssets(): LayoutAsset[] {
    return this.currentStage?.layoutAssets ?? [];
  }

  /**
   * Adapts the API BoardInfo + actionsConfig into the internal BoardConfig
   * used by the board renderer.
   */
  get boardConfig(): BoardConfig | undefined {
    if (!this.currentStage || !this.currentStage.pathConfig) {
      console.log(
        `Board Config cannot be defined because ${this.currentStage} or ${this.currentStage?.pathConfig} are empty`,
      );
      return undefined;
    }

    const actions = this.currentStage.actionsConfig?.actions ?? [];
    const actionMap = new Map(actions.map((a) => [a.uuid, a]));

    const nodes: Record<string, ActionInfo> = {};
    for (const [pos, uuid] of Object.entries(
      this.currentStage.pathConfig.nodes,
    )) {
      nodes[pos] = actionMap.get(uuid)!;
    }

    return {
      length: this.currentStage.pathConfig.nodesCount,
      settings: this.currentStage.pathSettings,
      nodes,
    };
  }

  getColorHex(key: ColorKey): string | undefined {
    return this._colors[key];
  }

  getColor(key: ColorKey): number | undefined {
    const color = this.getColorHex(key);
    if (color) {
      return Number(color.replace('#', '0x'));
    }
    return;
  }

  setGameResponse(response: GameResponse): void {
    this._gameResponse = response;
  }

  setSessionParams(params: SessionParams): void {
    this._sessionParams = params;
  }

  setGameConfig(config: GameConfig): void {
    this.gameConfig = this.formatGameResponse(config);
    if (!this.gameConfig) {
      console.log(`Game config cannot be null here ${config}`);
    }

    if (config.stage.theme) {
      const vars = config.stage.theme.cssVariables ?? [];
      this._colors = Object.fromEntries(vars.map((v) => [v.key, v.value]));
      this.setCssVariables(vars);
    }
  }

  getActionByNodePosition(position: number): ActionInfo | undefined {
    const actionUuid = this.actionNodes[String(position)];
    if (!actionUuid) return;
    return this.getActionByUuid(actionUuid);
  }

  getActionByUuid(uuid: string): ActionInfo | undefined {
    return this.actions.find((a) => a.uuid === uuid);
  }

  setCssVariables(cssVariables: CssVariable[]): void {
    const id = 'splash-theme';
    let styleEl = document.getElementById(id) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = id;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `:root {${getBaseCssVariables(cssVariables as ConfigValue[]).join('\n')}}`;
  }

  getRulesData(): Partial<Rules> {
    const allActions = this.currentStage?.actionsConfig?.actions ?? [];
    const gameSymbols = allActions.filter(
      (a) =>
        a.actionType !== ActionType.Collectible &&
        a.actionType !== ActionType.Finish &&
        a.actionType !== ActionType.Checkpoint,
    );
    const instantWinTypes = [ActionType.Prize];
    const instantWinPrizes = allActions.filter((c) =>
      instantWinTypes.includes(c.actionType),
    );
    return {
      stageName: this.currentStage!.name.toUpperCase(),
      collectibles: this.currentStage?.collectiblesConfig?.collectibles ?? [],
      gameSymbols,
      instantWin: instantWinPrizes,
    };
  }

  private formatGameResponse(config: GameConfig): GameConfig {
    if (!config || !config.stage) return config;
    const translationService = new TranslationService();

    return {
      ...config,
      stage: {
        ...config.stage,
        actionsConfig: {
          ...config.stage.actionsConfig!,
          actions: this.getActions(
            config.stage.actionsConfig?.actions ?? [],
            translationService,
          ),
        },
        collectiblesConfig: {
          ...config.stage.collectiblesConfig!,
          collectibles: this.getCollectibles(
            config.stage.collectiblesConfig?.collectibles ?? [],
          ),
        },
      },
    };
  }

  private getActions(
    actions: ActionInfo[],
    trs: TranslationService,
  ): ActionInfo[] {
    return actions.map((a) => {
      const prizeLabel = a.prize
        ? this.getPrizeLabel(a.prize)
        : this.getPoints(trs, a.points);
      return {
        ...a,
        prize: a.prize ? { ...a.prize, label: prizeLabel } : null,
        collectible: a.collectible
          ? {
              ...a.collectible,
              prize: {
                ...a.collectible.prize,
                label: this.getPrizeLabel(a.collectible.prize),
              },
            }
          : null,
        prizeLabel, // prize or points label
      };
    });
  }

  private getPoints(trs: TranslationService, points?: number): string {
    if (points !== null && points !== undefined) {
      return `${points} ${trs.translate('points.pts').toUpperCase()}`;
    }
    return '';
  }

  private getCollectibles(collectibles: CollectibleInfo[]): CollectibleInfo[] {
    return collectibles.map((c) => ({
      ...c,
      prize: {
        ...c.prize,
        label: this.getPrizeLabel(c.prize),
      },
    }));
  }

  private getPrizeLabel(prize: UserPrize): string {
    const cashAmount = this.getValueWithCurrency(prize.value, prize.currency);
    if (prize.type === PrizeType.CASH) {
      return cashAmount;
    } else if (prize.type === PrizeType.CUSTOM) {
      return prize.label?.replace('{{value}}', cashAmount) || '';
    }
    return prize.label || '';
  }

  private getValueWithCurrency(value: number, currency: string): string {
    try {
      const locale = Intl.DateTimeFormat().resolvedOptions().locale;
      const currencyFormat = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
      });
      return currencyFormat.format(value);
    } catch {
      return `${value} ${currency}`;
    }
  }
}
